import { SearchBar } from "@/components/nav/SearchBar";
import { Disclosure } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import CHAINS, {
  DESTINATION_CHAINS,
  SourceChain,
  SOURCE_CHAINS,
} from "lib/chains";
import Link from "next/link";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction } from "react";
import MobileNavLink from "./MobileNavLink";
import MobileNavMenu from "./MobileNavMenu";
import { NavLink } from "./NavLink";
import NavMenu from "./NavMenu";
import NavSelector from "./NavSelector";

export type NavItem = { id: string; name: string; path: string };

const navigation: NavItem[] = [
  { id: "dashboard", name: "Dashboard", path: "/" },
  { id: "messages", name: "Messages", path: "/messages" },
];

export default function NavBar({
  sourceChain,
  setSourceChain,
}: {
  sourceChain: SourceChain;
  setSourceChain: Dispatch<SetStateAction<SourceChain>>;
}) {
  const chainLinks = (DESTINATION_CHAINS[sourceChain] || []).map((c) => ({
    id: c,
    name: CHAINS[c]?.name || "",
    path: "/chain/" + c,
  }));
  const { query } = useRouter();

  return (
    <Disclosure as="nav" className="lg:border-none">
      {({ open, close }) => (
        <>
          {/* Show search bar by itself at the top on mobile (rugged from etherscan) */}
          <div className="block w-full p-2 pb-0 md:hidden">
            <SearchBar className="w-full" />
          </div>
          <div className="mx-auto mt-3 max-w-7xl overflow-x-auto px-2 sm:px-4 lg:px-8">
            <div className="relative flex h-16 items-center justify-between">
              <div className="flex items-center px-2 lg:px-0">
                <div className="flex-shrink-0">
                  <Link href="/">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="/telepathy.svg"
                      alt="Telepathy logo"
                      className="mb-1"
                    />
                  </Link>
                </div>
                <div className="hidden lg:ml-4 lg:block">
                  <div className="flex space-x-2">
                    {navigation.map((item) => (
                      <NavLink
                        key={item.name}
                        name={item.name}
                        path={item.path}
                      />
                    ))}
                    <NavMenu
                      path="/chain/[chain]"
                      items={chainLinks}
                      activeItem={query?.chain as string | undefined}
                    >
                      Light Clients
                    </NavMenu>
                  </div>
                </div>
              </div>
              <div className="flex flex-1 items-center justify-end space-x-2 pl-2 pr-2 lg:ml-6 lg:pr-0">
                <SearchBar className="hidden w-full md:block" />
                <NavSelector
                  items={SOURCE_CHAINS.map((c) => ({
                    key: c,
                    name: CHAINS[c].name,
                  }))}
                  label={CHAINS[sourceChain].name}
                  activeItem={sourceChain}
                  setActiveItem={setSourceChain}
                />
              </div>
              <div className="flex lg:hidden">
                {/* Mobile menu button */}
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md bg-succinct-teal-10 p-2 text-succinct-teal hover:bg-succinct-teal-20 hover:bg-opacity-75 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-succinct-black">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>
            </div>
          </div>

          <Disclosure.Panel className="lg:hidden">
            <div className="space-y-1 px-2 pt-2 pb-3">
              {navigation.map((item) => (
                <MobileNavLink
                  key={item.id}
                  path={item.path}
                  name={item.name}
                />
              ))}

              <MobileNavMenu
                close={close}
                items={chainLinks}
                activeItem={query?.chain as string | undefined}
              />
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
